import {action, makeObservable, observable, runInAction} from "mobx";
import {ITransactionsDomainStore} from "../../domainStore/pages/transactionsDomainStore";
import {IModalStore} from "../global/modalStore";
import {addMonths} from "date-fns";
import ProcessedBookingGroups from "../../domainStore/pages/models/processedBookingGroups";

export interface ITransactionsUiStore {
  /**
   * ID of an event that is currently expanded.
   */
  expandedEventId: string;
  /**
   * Triggers loading of transactions from domain store.
   */
  loadTransactions: () => Promise<void>;
  /**
   * Flag to indicate when transactions are loading.
   */
  isLoadingTransactions: boolean;
  /**
   * Method that is triggered when a village is changed from the villages dropdown.
   */
  onSelectedVillageChange: () => Promise<void>;
  /**
   * This stores the date for whose month the transactions are to be fetched.
   */
  selectedDate: Date;
  /**
   * True if the user is switching to another village.
   */
  isSwitchingVillage: boolean;
  /**
   * Changes the month for which the transactions are to be loaded.
   * @param moveTo Month to which user wants to move to
   */
  changeSelectedMonthTo: (monthToAdd: number) => void;
  /**
   * A list of processed bookings.
   */
  transactions: ProcessedBookingGroups;

  /**
   * Reset the sort state of the table.
   */
  resetSortState: () => void;

  /**
   * Set the id of the currently expanded event. If the id is same as the current expanded event id, it will collapse the event.
   * @param id
   * @param isExpanded
   */
  setExpandedEventId: (id: string) => void;
}

export class TransactionsUiStore implements ITransactionsUiStore {
  private transactionsDomainStore: ITransactionsDomainStore;
  private modalStore: IModalStore;

  expandedEventId: string;
  isLoadingTransactions: boolean;
  selectedDate: Date;
  isSwitchingVillage = false;
  private transactionsRequestId = 0;
  private villageChangeRequestId = 0;

  transactions: ProcessedBookingGroups;

  constructor(
    transactionsDomainStore: ITransactionsDomainStore,
    modalStore: IModalStore
  ) {
    makeObservable(this, {
      expandedEventId: observable,
      isLoadingTransactions: observable,
      selectedDate: observable,
      loadTransactions: action,
      onSelectedVillageChange: action,
      isSwitchingVillage: observable,
      transactions: observable,
      resetSortState: action,
      setExpandedEventId: action,
      changeSelectedMonthTo: action
    });

    this.expandedEventId = "";
    this.isLoadingTransactions = false;
    this.transactionsDomainStore = transactionsDomainStore;
    const currentDate = new Date();
    this.selectedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );
    this.modalStore = modalStore;
    this.transactions = new ProcessedBookingGroups([], 0, 0, false);
  }

  loadTransactions = async () => {
    const requestId = ++this.transactionsRequestId;
    runInAction(() => {
      this.isLoadingTransactions = true;
    });

    const firstDayOfSelectedMonth = new Date(
      this.selectedDate.getFullYear(),
      this.selectedDate.getMonth(),
      1
    );
    const lastDaySelectedMonth = addMonths(firstDayOfSelectedMonth, 1);

    try {
      await this.transactionsDomainStore.loadTransactions(
        firstDayOfSelectedMonth,
        lastDaySelectedMonth
      );
      if (requestId === this.transactionsRequestId) {
        runInAction(() => {
          this.transactions = this.transactionsDomainStore.transactionsList;
        });
      }
    } catch (error) {
      if (requestId === this.transactionsRequestId) {
        runInAction(() => {
          this.transactions = new ProcessedBookingGroups([], 0, 0, false);
        });
        throw error;
      }
    } finally {
      if (requestId === this.transactionsRequestId) {
        runInAction(() => {
          this.isLoadingTransactions = false;
        });
      }
    }
  };

  onSelectedVillageChange = async () => {
    const requestId = ++this.villageChangeRequestId;
    runInAction(() => {
      this.isSwitchingVillage = true;
    });
    try {
      await this.loadTransactions();
    } catch (error) {
      this.modalStore.openErrorModal(error as Error);
    }
    if (requestId === this.villageChangeRequestId) {
      runInAction(() => {
        this.isSwitchingVillage = false;
      });
    }
  };

  setExpandedEventId = (id: string) => {
    this.expandedEventId = this.expandedEventId === id ? "" : id;
  };

  changeSelectedMonthTo = (monthToAdd: number) => {
    runInAction(() => {
      const nextDate = new Date(this.selectedDate);
      nextDate.setMonth(nextDate.getMonth() + monthToAdd, 1);
      this.selectedDate = nextDate;
    });
    this.loadTransactions().catch((error) =>
      this.modalStore.openErrorModal(error as Error)
    );
  };

  resetSortState = () => {
    this.expandedEventId = "";
  };
}
